import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import api from "@/lib/apiClient";
import SEO from "@/components/SEO";
import PilRow from "@/components/PilRow";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

export default function PilArchive() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ courts: [], years: [], categories: [], statuses: [] });
  const [q, setQ] = useState(params.get("q") || "");
  const page = parseInt(params.get("page") || "1");
  const court = params.get("court") || "";
  const year = params.get("year") || "";
  const category = params.get("category") || "";
  const status = params.get("status") || "";

  useEffect(() => { api.get("/pils/filters").then(({ data }) => setFilters(data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ page: String(page), limit: "10" });
    if (params.get("q")) p.set("q", params.get("q"));
    if (court) p.set("court", court);
    if (year) p.set("year", year);
    if (category) p.set("category", category);
    if (status) p.set("status", status);
    try {
      const { data } = await api.get(`/pils?${p.toString()}`);
      setItems(data.items); setTotal(data.total);
    } catch {} finally { setLoading(false); }
  }, [params, page, court, year, category, status]);

  useEffect(() => { load(); }, [load]);

  const update = (key, value) => {
    const np = new URLSearchParams(params);
    if (value && value !== "__all") np.set(key, value); else np.delete(key);
    np.set("page", "1");
    setParams(np);
  };
  const onSearch = (e) => { e.preventDefault(); update("q", q); };
  const clearAll = () => { setQ(""); setParams(new URLSearchParams()); };
  const totalPages = Math.ceil(total / 10);

  return (
    <div className="fade-in">
      <SEO title="Public Interest Litigations — NPST" description="Searchable archive of public-interest litigations instituted by the National Public Service Trust." canonical={`${window.location.origin}/pil`} />
      <section className="border-b border-npst-line paper-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="label-eyebrow text-npst-gold mb-3">Public Legal Archive</div>
          <h1 className="font-serif text-4xl sm:text-6xl font-semibold text-npst-ink tracking-tight">Public Interest Litigations</h1>
          <p className="mt-5 text-lg text-npst-inkSoft max-w-2xl leading-relaxed">A searchable public archive of litigation instituted and pursued by the Trust, with orders, timelines and present status.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <form onSubmit={onSearch} className="flex gap-3 mb-6">
          <div className="flex-1 flex items-center gap-3 px-4 border border-npst-line rounded-md bg-white focus-within:border-npst-gold transition-colors">
            <Search size={18} className="text-npst-muted" />
            <input data-testid="pil-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title, PIL code, case number, court or keyword…" className="flex-1 py-3 outline-none bg-transparent text-npst-ink" />
          </div>
          <Button type="submit" data-testid="pil-search-btn" className="bg-npst-navy hover:bg-npst-navyDeep text-white px-6">Search</Button>
        </form>

        <div className="flex flex-wrap items-center gap-3 mb-8 pb-6 border-b border-npst-line">
          <span className="inline-flex items-center gap-2 label-eyebrow text-npst-muted"><SlidersHorizontal size={13} /> Filter</span>
          {[
            { key: "court", opts: filters.courts, ph: "Court" },
            { key: "year", opts: filters.years, ph: "Year" },
            { key: "category", opts: filters.categories, ph: "Category" },
            { key: "status", opts: filters.all_statuses || filters.statuses, ph: "Status" },
          ].map((f) => (
            <Select key={f.key} value={params.get(f.key) || "__all"} onValueChange={(v) => update(f.key, v)}>
              <SelectTrigger data-testid={`filter-${f.key}`} className="w-[160px] h-10 bg-white border-npst-line text-sm"><SelectValue placeholder={f.ph} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All {f.ph}</SelectItem>
                {(f.opts || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          ))}
          {(court || year || category || status || params.get("q")) && (
            <button onClick={clearAll} data-testid="clear-filters" className="text-sm text-npst-gold hover:underline">Clear all</button>
          )}
          <span className="ml-auto text-sm text-npst-muted">{total} matter{total !== 1 ? "s" : ""}</span>
        </div>

        {loading ? (
          <div className="py-20 text-center text-npst-muted animate-pulse">Loading archive…</div>
        ) : items.length === 0 ? (
          <div className="py-20 text-center" data-testid="pil-empty">
            <p className="font-serif text-2xl text-npst-ink">No matters found</p>
            <p className="text-npst-muted mt-2">Adjust your search or filters to explore the archive.</p>
          </div>
        ) : (
          <div>{items.map((p, i) => <PilRow key={p.id} pil={p} index={i} />)}</div>
        )}

        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-12">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button key={n} onClick={() => update("page", String(n))} data-testid={`page-${n}`}
                className={`w-10 h-10 rounded-md text-sm font-medium transition-colors ${n === page ? "bg-npst-navy text-white" : "border border-npst-line text-npst-inkSoft hover:border-npst-gold"}`}>
                {n}
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
